import { OpenFoodFacts, type ProductV2 } from "@openfoodfacts/openfoodfacts-nodejs";
import { Store } from "@tanstack/react-store";
import { has, isEmpty, reduce } from "lodash";
import type { ProductV3, SearchResultV3 } from "../types";
import { cachedFetch } from "../utils/cached-fetch";
import { localStorage } from "../utils/local-storage";
import { LRUCache } from "../utils/lru-cache";
import { favoriteProductsIndexStore } from "./favorite-products";

// Constants for localStorage keys
export const PRODUCTS_INDEX_KEY = "products_index";
export const PRODUCT_KEY_PREFIX = "product_";
export const MAX_PRODUCTS = 500;

// Legacy storage key for favorites
export const LEGACY_FAVORITES_KEY = "favoriteProducts";

// Store that contains product index as a hash map of product code to timestamp
export const productIndexStore = new Store<Record<string, number>>({});

// Function to get product localStorage key
export const getProductKey = (productCode: string): string => {
  return `${PRODUCT_KEY_PREFIX}${productCode}`;
};

// Function to ensure index stays within size limit by removing oldest entries
const enforceProductLimit = (index: Record<string, number>): Record<string, number> => {
  const entries = Object.entries(index);
  if (entries.length <= MAX_PRODUCTS) {
    return index;
  }

  // Sort by timestamp (oldest first) and keep only the newest MAX_PRODUCTS entries
  const sortedEntries = entries.sort(([, a], [, b]) => a - b);
  const entriesToRemove = sortedEntries.slice(0, entries.length - MAX_PRODUCTS);

  // Remove the products from localStorage
  entriesToRemove.forEach(([code]) => {
    localStorage?.removeItem(getProductKey(code));
  });

  // Return object with only the newest MAX_PRODUCTS entries
  return Object.fromEntries(sortedEntries.slice(entries.length - MAX_PRODUCTS));
};

// Load product index from localStorage and handle migration of old data
export const loadProductIndex = (): void => {
  // Try to load from the new format first
  let indexLoaded = false;
  const indexJson = localStorage?.getItem(PRODUCTS_INDEX_KEY);
  const now = Date.now();

  if (indexJson && indexJson !== "undefined") {
    try {
      const index = JSON.parse(indexJson) as Record<string, boolean | number>;
      if (typeof index === "object" && !Array.isArray(index)) {
        // Convert any boolean values to timestamps
        const timestampIndex = Object.entries(index).reduce((acc, [key, value]) => {
          acc[key] = typeof value === "boolean" ? now : (value as number);
          return acc;
        }, {} as Record<string, number>);

        productIndexStore.state = enforceProductLimit(timestampIndex);
        indexLoaded = true;
        console.log(`Loaded ${Object.keys(productIndexStore.state).length} products from index`);
      } else if (Array.isArray(index)) {
        // Migrate from array format to timestamp object format
        const indexMap = index.reduce((acc, code) => {
          acc[code] = now;
          return acc;
        }, {} as Record<string, number>);
        productIndexStore.state = enforceProductLimit(indexMap);
        indexLoaded = true;
        console.log(`Migrated ${index.length} products from array to timestamp index`);
      }
    } catch (e) {
      console.error("Failed to parse product index:", e);
      productIndexStore.state = {};
    }
  }

  // If no index was loaded or it's empty, try to migrate from old formats
  if (!indexLoaded || isEmpty(productIndexStore.state)) {
    // Track all product codes we find in legacy stores
    const productMap: Record<string, number> = {};

    // Function to extract products from legacy storage
    const extractProductsFromLegacy = (storageKey: string): void => {
      const json = localStorage?.getItem(storageKey);
      if (json && json !== "undefined") {
        try {
          const products = JSON.parse(json) as Record<string, ProductV3>;

          reduce(
            products,
            (result, product) => {
              if (has(product, "code")) {
                // Store the product in the new format
                storeProduct(product);
                productMap[product.code!] = now;
              }
              return result;
            },
            {}
          );

          console.log(`Migrated products from ${storageKey}`);
        } catch (e) {
          console.error(`Failed to migrate products from ${storageKey}:`, e);
        }
      }
    };

    // Extract products from legacy favorites only
    extractProductsFromLegacy(LEGACY_FAVORITES_KEY);

    // Update the index with all found product codes
    if (!isEmpty(productMap)) {
      productIndexStore.setState(() => enforceProductLimit(productMap));
      console.log(`Migrated ${Object.keys(productMap).length} products from legacy stores`);
      saveProductIndex();
    }
  }

  // Verify all products in the index actually exist
  const validProductMap: Record<string, number> = {};
  let invalidCount = 0;

  Object.entries(productIndexStore.state).forEach(([code, timestamp]) => {
    const exists = !!getProduct(code);
    if (exists) {
      validProductMap[code] = timestamp;
    } else {
      console.warn(`Product ${code} in index but not found in storage`);
      invalidCount++;
    }
  });

  // Update index if we removed any invalid entries
  if (invalidCount > 0) {
    console.log(`Removed ${invalidCount} invalid products from index`);
    productIndexStore.setState(() => validProductMap);
    saveProductIndex();
  }
};

// Save product index to localStorage
export const saveProductIndex = (): void => {
  console.log("Saving product index:", productIndexStore.state);
  localStorage?.setItem(PRODUCTS_INDEX_KEY, JSON.stringify(productIndexStore.state));
};

// Subscribe to changes in the product index and save to localStorage
productIndexStore.subscribe(() => {
  console.log("productIndexStore updated");
  saveProductIndex();
});

// --- LRU in-memory product cache (max 256, evicts least recently used) ---
const PRODUCT_CACHE_MAX = 256;
const lrucache = new LRUCache<string, ProductV3>(PRODUCT_CACHE_MAX);

// Add or update a product in localStorage and cache
export const storeProduct = (product: ProductV3 | ProductV2, forceCode?: string): boolean => {
  console.log("storeProduct", product, forceCode);
  if (!product.code) return false;
  try {
    // Store the product in localStorage
    const productKey = getProductKey(forceCode ?? product.code);
    localStorage?.setItem(productKey, JSON.stringify(product));

    // Update cache
    const productCode = forceCode ?? product.code;
    lrucache.set(productCode, product as ProductV3);

    // Only update index if product not already present
    const now = Date.now();
    if (!productIndexStore.state[productCode]) {
      productIndexStore.setState((prevIndex) => {
        const newIndex = {
          ...prevIndex,
          [productCode]: now,
        };
        return enforceProductLimit(newIndex);
      });
    }

    return true;
  } catch (e) {
    console.error("Failed to store product:", e);
    return false;
  }
};

// Get a product from cache first, then localStorage if not cached
export const getProduct = (productCode: string): ProductV3 | null => {
  if (!productCode) return null;
  // Check cache first (LRU)
  const cached = lrucache.get(productCode);
  if (cached) {
    return cached;
  }
  try {
    const productKey = getProductKey(productCode);
    const productJson = localStorage?.getItem(productKey);
    if (productJson && productJson !== "undefined") {
      const product = JSON.parse(productJson) as ProductV3;
      // Insert into LRU cache
      lrucache.set(productCode, product);
      return product;
    }
  } catch (e) {
    console.error(`Failed to get product ${productCode}:`, e);
  }
  return null;
};

const off = new OpenFoodFacts(cachedFetch);
export const fetchProduct = async (productCode: string): Promise<ProductV3 | null> => {
  const oldProduct = getProduct(productCode);

  return off
    .getProduct(productCode)
    .then((product) => product as ProductV3 | undefined)
    .then((product) => {
      if (!product?.code) {
        return null;
      }

      if (product && product.code !== productCode) {
        storeProduct(product as ProductV3, productCode);
      }

      if (oldProduct?.last_updated_t !== product.last_updated_t) {
        storeProduct(product);
      }

      return product;
    });
};

export const searchProducts = async (searchTerm: string, page: number): Promise<SearchResultV3> => {
  const encodedSearchTerm = encodeURIComponent(searchTerm);
  const encodedSearchQuery = encodeURIComponent(
    searchTerm + " nutriments.energy-kcal_100g:[1 TO *]"
  );

  // return cachedFetch(`https://edealer.nl/cors/index.php?_log=${encodedSearchTerm}__${page}`, {
  // return cachedFetch(`http://localhost:8787/cors/index.php?_log=${encodedSearchTerm}__${page}`, {
  // return cachedFetch(`https://search.openfoodfacts.org/search?page=${page}&page_size=25&q=${encodedSearchQuery}&boost_phrase=${encodedSearchTerm}&_log=${encodedSearchTerm}__${page}`, {
  return cachedFetch(`/cors/?_log=${encodedSearchTerm}__${page}`, {
    headers: {
      "X-Proxy-URL": encodeURI(
        `https://search.openfoodfacts.org/search?page=${page}&page_size=25&q=${encodedSearchQuery}&boost_phrase=${encodedSearchTerm}`
      ),
    },
  })
    .then((res) => res.json())
    .then((response) => response as SearchResultV3)
    .then((response) => {
      if (!response?.products) {
        if (response.hits) {
          response.products = response.hits;
        } else {
          throw new Error("Invalid response from OpenFoodFacts API");
        }
      }
      return response;
    });
};

// Get multiple products from localStorage
export const getProducts = (productCodes: string[]): Record<string, ProductV3> => {
  return reduce(
    productCodes,
    (products, code) => {
      const product = getProduct(code);
      if (product) {
        products[code] = product;
      }
      return products;
    },
    {} as Record<string, ProductV3>
  );
};

// Remove a product from localStorage and the index
export const removeProduct = (productCode: string): boolean => {
  try {
    // Remove from localStorage
    const productKey = getProductKey(productCode);
    localStorage?.removeItem(productKey);
    lrucache.delete(productCode);

    // Remove from index
    productIndexStore.setState((prevIndex) => {
      const newIndex = { ...prevIndex };
      delete newIndex[productCode];
      return newIndex;
    });

    return true;
  } catch (e) {
    console.error(`Failed to remove product ${productCode}:`, e);
    return false;
  }
};

export const hasProduct = (productCode: string): boolean => {
  if (!productCode) return false;
  return !!productIndexStore.state[productCode];
};

// Clear all products from localStorage
export const clearAllProducts = (): void => {
  try {
    if (localStorage) {
      // Remove all local storage items starting with prefix and not in favorites.
      Object.keys(localStorage).forEach((key) => {
        const productCode = key.replace(PRODUCT_KEY_PREFIX, "");
        if (
          key.startsWith(PRODUCT_KEY_PREFIX) &&
          !favoriteProductsIndexStore.state.has(productCode)
        ) {
          localStorage?.removeItem(key);
          delete productIndexStore.state[productCode];
        }
      });

      saveProductIndex();
    }
  } catch (e) {
    console.error("Failed to clear all products:", e);
  }
};

export const getProductCategories = (product: ProductV3): string[] => {
  return [
    ...(product.categories_tags ?? []),
    ...(product.food_groups_tags ?? []),
    product.product_name,
    product.product_name_en,
  ].filter((c): c is string => !!c && !c.includes("-based-") && !c.includes("-and-"));
};

export const getMacros = (
  product: ProductV3
): { energy: number; proteins: number; carbs: number; fat: number } => {
  return {
    energy: product.nutriments?.["energy-kcal_100g"] ?? product.nutriments?.["energy-kcal"] ?? 0,
    fat: product.nutriments?.fat_100g ?? product.nutriments?.fat ?? 0,
    carbs: product.nutriments?.carbohydrates_100g ?? product.nutriments?.carbohydrates ?? 0,
    proteins: product.nutriments?.proteins_100g ?? product.nutriments?.proteins ?? 0,
  };
};
